import { computed, ref, watch } from 'vue';
import { gql } from 'graphql-tag';
import uniqBy from 'lodash/uniqBy';
import {
	useSettingsQuery,
	useSettingSaveMutation,
	type Scalars,
} from '@/generated/graphql';
import { type ValueOf } from '@/types';
import { type Constants } from '@/constants';
import { parseBooleanString, stringifyBoolean } from '@/utils/sapi';
import { type Setting } from '@/constants/settings';

type SettingName = ValueOf<typeof Constants.Setting>;

/**
 * A composable to retrieve user settings
 *
 * Usage example:
 * ```ts
 * const { setting } = useSettings();
 *
 * const dateFormattingSetting = setting(Setting.LOCALE_DATE);
 * ```
 *
 * Most of the time it is safe to assume that the result of template will
 * be depending on the setting, for example, date format. In such case,
 * use the computed property to get the setting
 * ```ts
 * const userDateFormat = computed(() => setting(Setting.LOCALE_DATE));
 * ```
 */
export const useSettings = () => {
	const { result, ...rest } = useSettingsQuery({
		fetchPolicy: 'cache-first',
	});

	const setting = (name: SettingName) => {
		return result.value?.CurrentUserSettings.find(
			(setting) => setting.name === name
		)?.value;
	};

	return {
		setting,
		...rest,
	};
};

/**
 * To make using a single setting a little more ergonomic.
 *
 * @example
 * ```ts
 * const [mySetting] = useSetting(Setting.LOCALE_DATE, 'default');
 * ```
 */
export const useSetting = (name: SettingName, defaultValue?: string) => {
	const { setting, loading, ...rest } = useSettings();

	const settingValue = computed(() => {
		if (!loading.value) {
			return setting(name) ?? defaultValue;
		}
		return defaultValue;
	});

	return [settingValue, { loading, ...rest }] as const;
};

/**
 * Get the value of a single boolean setting ("0" or "1" in the database)
 * parsed to a boolean.
 *
 * @example
 * ```ts
 * const [useRolesSetting] = useBooleanSetting(Setting.USE_ROLES);
 * ```
 */
export const useBooleanSetting = (name: SettingName, defaultValue = false) => {
	const [value, extra] = useSetting(name, stringifyBoolean(defaultValue));

	const parsedValue = computed(() => parseBooleanString(value.value));

	return [parsedValue, extra] as const;
};

/**
 * Same as useBooleanSetting, just used for opt out settings
 * where the default value is false meaning something is enabled
 *
 * @example
 * ```ts
 * const [disableFallback] = useBooleanSetting(Setting.DISABLE_ELI_CHAT);
 * ```
 */
export const useInvertedBooleanSetting = (
	name: SettingName,
	defaultValue = false
) => {
	const [value, extra] = useSetting(name, stringifyBoolean(defaultValue));

	const parsedValue = computed(() => !parseBooleanString(value.value));

	return [parsedValue, extra] as const;
};

/**
 * Create a new `ref` but use a setting as the original value.
 *
 * Useful for settings pages, for example, which need to initialize a setting
 * value, and then allow the user to modify it.
 */
export const useInitializedBooleanRefFromSetting = (
	name: SettingName,
	defaultValue = false,
	isInvertedSetting = false
) => {
	let [value, extra] = useBooleanSetting(name, defaultValue);

	if (isInvertedSetting) {
		[value, extra] = useInvertedBooleanSetting(name, defaultValue);
	}

	const settingRef = ref(value.value);

	const isInitialized = ref(false);

	extra.onResult(() => {
		if (isInitialized.value || extra.loading.value) {
			return;
		}

		settingRef.value = value.value;
		isInitialized.value = true;
	});

	return [settingRef, { ...extra, originalValue: value }] as const;
};

/**
 * Update any settings.
 */
export const useSettingsSave = () => {
	const { mutate, loading, onDone } = useSettingSaveMutation({
		fetchPolicy: 'no-cache',
		update(cache, { data }) {
			if (!data?.SettingSave) {
				return;
			}

			cache.evict({
				fieldName: 'SearchTotals',
			});

			cache.evict({
				fieldName: 'SearchResults',
			});

			cache.gc();

			cache.modify({
				fields: {
					CurrentUserSettings(existingSettings = []) {
						// If the setting is disabled, then it might be missing
						// from `CurrentUserSettings` array, so we can't rely
						// on Apollo Cache updating it automatically.
						const ref = cache.writeFragment({
							fragment: gql`
								fragment MySetting on Setting {
									name
									value
								}
							`,
							data: data.SettingSave,
						});

						return uniqBy([...existingSettings, ref], '__ref');
					},
				},
			});
		},
	});

	const saveSetting = (
		name: ValueOf<typeof Setting>,
		value: Scalars['SettingValue']['input']
	) => {
		return mutate({
			input: {
				name,
				value,
			},
		});
	};

	return {
		saveSetting,
		loading,
		onDone,
	};
};

/**
 * Get the ref(value) of a single boolean setting ("0" or "1" in the database)
 * parsed to a boolean. Once this ref is changed it automatically sends the mutation.
 *
 * @example
 * ```ts
 * const { setting: useRolesSetting } = useBooleanSettingUpdating(Setting.USE_ROLES);
 *
 * useRolesSetting.value = true; // It sends the mutation and auto strigify the boolean value
 * ```
 */
export const useBooleanSettingUpdating = (
	settingName: SettingName,
	isInvertedSetting = false
) => {
	const [setting, { originalValue, loading }] =
		useInitializedBooleanRefFromSetting(
			settingName,
			undefined,
			isInvertedSetting
		);

	const { saveSetting, onDone } = useSettingsSave();

	watch(setting, () => {
		if (!loading.value && originalValue.value !== setting.value) {
			saveSetting(
				settingName,
				stringifyBoolean(
					isInvertedSetting ? !setting.value : setting.value
				)
			);
		}
	});

	return {
		setting,
		onDone,
		loading,
		originalValue,
	};
};
